import React, { useState, useEffect } from "react";
import {
  IResourceComponentsProps,
  useTranslate,
  useShow,
  BaseRecord,
  useCreate,
  useInvalidate,
} from "@refinedev/core";
import {
  Show,
  NumberField,
  TextField,
  DateField,
  useTable,
  EmailField,
  TagField,
  EditButton,
  ShowButton,
  DeleteButton,
  useSelect,
} from "@refinedev/antd";
import { Row, Col, Tag, Card, Typography, Divider, Table, Space, Modal, Button, Form, Input, Select, Avatar, theme } from "antd";
import { CheckCircleOutlined, ClockCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { ITenant } from "../../models/tenant";
import { useAvatarPalette } from "../../shared/styles";
import { getAvatarColor, getInitials } from "../../shared/utils";
import {
  TenantAutomationFields,
  TenantApiKeyFields,
} from "../../components/tenant/TenantGeneralFields";

const { Title, Text } = Typography;

export const TenantShow: React.FC<IResourceComponentsProps> = () => {
  const _t = useTranslate();
  const { token } = theme.useToken();
  const avatarPalette = useAvatarPalette();
  const { queryResult } = useShow<ITenant>();
  const { data, isLoading } = queryResult;
  const record = data?.data;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const invalidate = useInvalidate();
  const { mutate: createUser } = useCreate();

  const { tableProps: usersTableProps, tableQueryResult } = useTable({
    resource: "users",
    syncWithLocation: false,
    queryOptions: {
      enabled: !!record?.id,
    },
    filters: {
      permanent: record?.id
        ? [
            {
              field: "tenantId",
              operator: "eq",
              value: record.id,
            },
          ]
        : [],
    },
  });

  const [form] = Form.useForm();

  const handleFormFinish = (values: any) => {
    const transformedValues = {
      ...values,
      tenantId: record?.id,
    };
    createUser(
      {
        resource: "users",
        values: transformedValues,
      },
      {
        onSuccess: () => {
          setIsModalOpen(false);
          form.resetFields();
          invalidate({ resource: "users", invalidates: ["list"] });
          tableQueryResult?.refetch();
        },
        onError: (error: any) => {
          const errors = error?.response?.data?.errors;
          if (errors && 'email' in errors) {
            if (errors['email'] === 'emailAlreadyExists') {
              form.setFields([
                {
                  name: 'email',
                  errors: [_t('users.errors.emailAlreadyExists')],
                },
              ]);
            }
          }
        },
      }
    );
  };

  const { selectProps: roleSelectProps } = useSelect({
    resource: "roles",
    optionLabel: "name",
  });

  const { selectProps: accountSelectProps } = useSelect({
    resource: "accounts",
    optionLabel: "name",
  });

  useEffect(() => {
    if (isModalOpen) {
      form.setFieldsValue({
        provider: 'email',
        status: { id: 1 },
      });
    }
  }, [isModalOpen, form]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "green";
      case "suspended":
        return "red";
      case "trial":
        return "blue";
      default:
        return "default";
    }
  };

  const getSubscriptionColor = (subscriptionStatus: string) => {
    switch (subscriptionStatus) {
      case "active":
        return "green";
      case "trial":
        return "blue";
      default:
        return "default";
    }
  };

  return (
    <Show isLoading={isLoading}>
      <Card className="shadow-lg">
        <Row gutter={[{ xs: 8, sm: 16, md: 24 }, 16]}>
          <Col xs={24} sm={12} md={8}>
            <Text strong>{_t("tenants.fields.id", "ID")}: </Text>
            <NumberField value={record?.id ?? ""} />
          </Col>
        </Row>

        <Divider />

        <Row gutter={[{ xs: 8, sm: 16, md: 24 }, 16]}>
          <Col xs={24} sm={12} md={12}>
            <Text strong>{_t("tenants.fields.name", "Name")}: </Text>
            <TextField value={record?.name} />
          </Col>
          <Col xs={24} sm={12} md={12}>
            <Text strong>{_t("tenants.fields.slug", "Slug")}: </Text>
            <TextField value={record?.slug} />
          </Col>
        </Row>

        <Row gutter={[{ xs: 8, sm: 16, md: 24 }, 16]}>
          <Col xs={24} sm={12} md={12}>
            <Text strong>{_t("tenants.fields.status", "Status")}: </Text>
            {record?.status && (
              <Tag color={getStatusColor(record.status)}>
                {_t(
                  `tenants.fields.statusValues.${record.status}`,
                  record.status
                )}
              </Tag>
            )}
          </Col>
          <Col xs={24} sm={12} md={12}>
            <Text strong>
              {_t("tenants.fields.subscriptionStatus", "Subscription")}:{" "}
            </Text>
            {record?.subscriptionStatus && (
              <Tag color={getSubscriptionColor(record.subscriptionStatus)}>
                {_t(
                  `tenants.fields.subscriptionStatusValues.${record.subscriptionStatus}`,
                  record.subscriptionStatus
                )}
              </Tag>
            )}
          </Col>
        </Row>

        <Row gutter={[{ xs: 8, sm: 16, md: 24 }, 16]}>
          <Col xs={24} sm={12} md={12}>
            <Text strong>{_t("tenants.fields.activatedAt", "Activated on")}: </Text>
            {record?.activatedAt ? (
              <DateField value={record.activatedAt} format="DD/MM/YYYY HH:mm" />
            ) : (
              <TextField value="-" />
            )}
          </Col>
          <Col xs={24} sm={12} md={12}>
            <Text strong>{_t("tenants.fields.trialEndsAt", "Trial ends")}: </Text>
            {record?.trialEndsAt ? (
              <Tag
                color={
                  new Date(record.trialEndsAt).getTime() <= Date.now()
                    ? "red"
                    : "default"
                }
              >
                <DateField value={record.trialEndsAt} format="DD/MM/YYYY" />
              </Tag>
            ) : (
              <TextField value="-" />
            )}
          </Col>
        </Row>

        {record?.settings && (
          <>
            <Divider />
            <Row gutter={[{ xs: 8, sm: 16, md: 24 }, 16]}>
              <Col xs={24}>
                <Text strong>
                  {_t("tenants.fields.settings", "Settings")}:{" "}
                </Text>
                <pre className="bg-gray-50 p-4 rounded mt-2">
                  {JSON.stringify(record.settings, null, 2)}
                </pre>
              </Col>
            </Row>
          </>
        )}

        <Divider />
        
        <Form component={false}>
          <TenantAutomationFields variant="readonly" record={record} />
        </Form>
        <Row gutter={[{ xs: 8, sm: 16, md: 24 }, 16]}>
          <Col xs={24} sm={12} md={12}>
            <Text strong>{_t("tenants.fields.localApiEndpoint", "Local API Endpoint")}: </Text>
            <TextField value={record?.localApiEndpoint} />
          </Col>
        </Row>

        <Divider />

        <Form component={false}>
          <TenantApiKeyFields variant="readonly" record={record} />
        </Form>

        <Divider />

        <Row gutter={[{ xs: 8, sm: 16, md: 24 }, 16]}>
          <Col xs={24}>
            <Title level={5}>Alientech Credentials</Title>
          </Col>
          <Col xs={24} sm={12} md={12}>
            <Text strong>
              {_t("tenants.fields.alientechLogin", "Alientech Login")}:{" "}
            </Text>
            <TextField 
              value={
                record?.alientechLogin 
                  ? (typeof record.alientechLogin === "string" 
                      ? record.alientechLogin 
                      : String(record.alientechLogin))
                  : undefined
              } 
            />
          </Col>
          <Col xs={24} sm={12} md={12}>
            <Text strong>
              {_t("tenants.fields.alientechPassword", "Alientech Password")}:{" "}
            </Text>
            <Text type={record?.alientechPassword ? "secondary" : "secondary"}>
              {record?.alientechPassword 
                ? (typeof record.alientechPassword === "string" 
                    ? "••••••••" 
                    : "••••••••")
                : "Not set"}
            </Text>
          </Col>
        </Row>

        <Divider />

        <Row gutter={[{ xs: 8, sm: 16, md: 24 }, 16]}>
          <Col xs={24} sm={12} md={12}>
            <Text strong type="secondary">
              {_t("tenants.fields.createdAt", "Created At")}:{" "}
            </Text>
            <DateField
              value={record?.createdAt}
              format="DD/MM/YYYY HH:mm"
            />
          </Col>
          <Col xs={24} sm={12} md={12}>
            <Text strong type="secondary">
              {_t("tenants.fields.updatedAt", "Updated At")}:{" "}
            </Text>
            <DateField
              value={record?.updatedAt}
              format="DD/MM/YYYY HH:mm"
            />
          </Col>
        </Row>
      </Card>

      <Card className="shadow-lg mt-6">
        <div className="flex justify-between items-center mb-4">
          <Typography.Title level={4} className="mb-0">
            {_t("tenants.fields.users", "Users")}
          </Typography.Title>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setIsModalOpen(true)}
          >
            {_t("users.actions.create", "Create User")}
          </Button>
        </div>
        <Table {...usersTableProps} rowKey="id">
          <Table.Column
            dataIndex="id"
            title={_t("users.fields.id", "ID")}
          />
          <Table.Column
            title={_t("users.fields.firstName", "User")}
            key="user"
            width={280}
            render={(_, record: any) => (
              <Space>
                <Avatar
                  shape="square"
                  size={44}
                  src={record.photo?.path}
                  style={{
                    backgroundColor: record.photo?.path ? undefined : getAvatarColor(record.id, avatarPalette),
                    fontWeight: 600,
                  }}
                >
                  {record.photo?.path ? null : getInitials(record.firstName, record.lastName)}
                </Avatar>
                <div>
                  <Space size={4}>
                    <Typography.Text strong>{record.firstName} {record.lastName}</Typography.Text>
                    <Tag
                      color={
                        record.role?.name === "Superadmin" ? "red" :
                        record.role?.name === "Admin" ? "blue" :
                        record.role?.name === "Tuner" ? "purple" :
                        record.role?.name === "Service" ? "orange" : "default"
                      }
                    >
                      {_t(`users.fields.roleValues.${record.role?.name || "User"}`, record.role?.name || "User")}
                    </Tag>
                  </Space>
                  <br />
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {record.status?.name?.toLowerCase() === "active" ? (
                      <><CheckCircleOutlined style={{ color: token.colorSuccess, marginRight: 4 }} /> {_t(`users.fields.statusValues.${record.status?.name?.toLowerCase()}`, record.status?.name)}</>
                    ) : (
                      <><ClockCircleOutlined style={{ color: token.colorError, marginRight: 4 }} /> {_t(`users.fields.statusValues.${record.status?.name?.toLowerCase()}`, record.status?.name)}</>
                    )}
                  </Typography.Text>
                </div>
              </Space>
            )}
          />
          <Table.Column
            dataIndex={["email"]}
            title={_t("users.fields.email", "Email")}
            render={(value: any) => <EmailField value={value} copyable />}
          />
          <Table.Column
            dataIndex={["role", "name"]}
            title={_t("users.fields.role", "Role")}
            render={(value: any) => <TextField value={_t(`users.fields.roleValues.${value}`, value)} />}
          />
          <Table.Column
            title={_t("table.actions", "Actions")}
            dataIndex="actions"
            render={(_, record: BaseRecord) => (
              <Space>
                <EditButton
                  resource="users"
                  hideText
                  size="small"
                  recordItemId={record.id}
                />
                <ShowButton
                  resource="users"
                  hideText
                  size="small"
                  hidden
                  recordItemId={record.id}
                />
                <DeleteButton
                  resource="users"
                  hideText
                  size="small"
                  recordItemId={record.id}
                />
              </Space>
            )}
          />
        </Table>
      </Card>

      <Modal
        title={_t("users.actions.create", "Create User")}
        open={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          form.resetFields();
        }}
        footer={[
          <Button key="cancel" onClick={() => {
            setIsModalOpen(false);
            form.resetFields();
          }}>
            {_t("buttons.cancel", "Cancel")}
          </Button>,
          <Button key="submit" type="primary" onClick={() => form.submit()}>
            {_t("buttons.save", "Save")}
          </Button>,
        ]}
        width={600}
      >
        <Form layout="vertical" form={form} onFinish={handleFormFinish}>
          <Form.Item
            label={_t("users.fields.email")}
            name={["email"]}
            rules={[{ required: true, message: _t("forms.errors.required") }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label={_t("users.fields.password")}
            name={["password"]}
            rules={[
              { required: true, message: _t("forms.errors.required") },
              { min: 6, message: _t("fields.errors.minLength", { min: 6 }) },
            ]}
          >
            <Input.Password />
          </Form.Item>
          <Form.Item
            label={_t("users.fields.provider")}
            name={["provider"]}
            hidden
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label={_t("users.fields.firstName")}
            name={["firstName"]}
            rules={[{ required: true, message: _t("forms.errors.required") }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label={_t("users.fields.lastName")}
            name={["lastName"]}
            rules={[{ required: true, message: _t("forms.errors.required") }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label={_t("users.fields.role")}
            name={["role", "id"]}
            rules={[{ required: true, message: _t("forms.errors.required") }]}
          >
            <Select {...roleSelectProps} />
          </Form.Item>
          <Form.Item
            label={_t("users.fields.status")}
            name={["status", "id"]}
            hidden
            rules={[
              {
                required: true,
              },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label={_t("users.fields.account")}
            name={["account", "id"]}
            rules={[{ required: false }]}
          >
            <Select {...accountSelectProps} />
          </Form.Item>
        </Form>
      </Modal>
    </Show>
  );
};

